/**
 * RunActionDropdown component
 *
 * Dropdown menu for executing automated actions on a security issue.
 * Supports notification actions (Slack, Email), investigation actions
 * (Search Similar Prompts, Generate Incident Report), and containment
 * stubs (Coming Soon).
 *
 * Investigation results are displayed in dedicated components:
 * - SimilarTracesDialog: enriched trace display with link-to-issue
 * - IncidentReportPanel: Sheet panel with export-ready document report
 *
 * Cached results: On mount, fetches the latest completed investigation
 * results. If a cached result exists, the menu shows "View ..." instead
 * of "Generate ..." with a separate "Regenerate" / "Re-search" option.
 */

import { useState, useCallback } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Play,
  Bell,
  Mail,
  Users,
  Shield,
  Search,
  Wrench,
  FileText,
  Lock,
  AlertTriangle,
  Eye,
  Download,
} from "lucide-react";
import { useExecuteAction, useLatestActionResult } from "@/hooks/useIssueActions";
import { useSlackIntegration } from "@/hooks/useSlackIntegration";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { SimilarTracesDialog, type SimilarTrace } from "./SimilarTracesDialog";
import { IncidentReportPanel, type IncidentReport } from "./IncidentReportPanel";
import type { ActionType, ActionCategory } from "@/types/issueAction";

interface RunActionDropdownProps {
  issueId: number;
}

interface NotificationActionConfig {
  kind: "notification";
  type: ActionType;
  category: ActionCategory;
  label: string;
  parameterLabel: string;
  parameterKey: string;
  parameterPlaceholder: string;
  description: string;
}

interface InvestigationActionConfig {
  kind: "investigation";
  type: ActionType;
  category: ActionCategory;
  label: string;
  description: string;
}

const NOTIFICATION_CONFIGS: Record<string, NotificationActionConfig> = {
  notify_email: {
    kind: "notification",
    type: "notify_email",
    category: "notification",
    label: "Send Email Alert",
    parameterLabel: "Email Address",
    parameterKey: "email",
    parameterPlaceholder: "security-team@example.com",
    description:
      "Send an email notification about this security issue to the specified address.",
  },
};

const INVESTIGATION_CONFIGS: Record<string, InvestigationActionConfig> = {
  search_similar_prompts: {
    kind: "investigation",
    type: "search_similar_prompts",
    category: "investigation",
    label: "Search Similar Prompts",
    description:
      "Search for telemetry traces with similar content or patterns to this security issue.",
  },
  generate_incident_report: {
    kind: "investigation",
    type: "generate_incident_report",
    category: "investigation",
    label: "Generate Incident Report",
    description:
      "Generate a comprehensive report with AI-generated analysis, traces, comments, activities, and tickets.",
  },
};

export function RunActionDropdown({ issueId }: RunActionDropdownProps) {
  // Org-level integration checks
  const { data: slackIntegration } = useSlackIntegration();
  const { organization } = useOrganizationContext();
  const slackConfigured = slackIntegration != null && slackIntegration.is_active;
  const teamsConfigured = !!organization?.teams_webhook_url_last_four;

  // Notification dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationActionConfig | null>(null);
  const [parameterValue, setParameterValue] = useState("");

  // Similar Traces dialog state
  const [similarTracesDialogOpen, setSimilarTracesDialogOpen] = useState(false);
  const [similarTraces, setSimilarTraces] = useState<SimilarTrace[]>([]);
  const [similarTracesTotal, setSimilarTracesTotal] = useState(0);

  // Incident Report panel state
  const [reportPanelOpen, setReportPanelOpen] = useState(false);
  const [reportData, setReportData] = useState<IncidentReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const executeAction = useExecuteAction();

  // Fetch cached investigation results on mount
  const latestReport = useLatestActionResult(issueId, "generate_incident_report");
  const latestSearch = useLatestActionResult(issueId, "search_similar_prompts");

  const hasReportCache =
    latestReport.data?.status === "COMPLETED" &&
    (latestReport.data?.result as Record<string, unknown> | null)?.report != null;
  const hasSearchCache =
    latestSearch.data?.status === "COMPLETED" &&
    (latestSearch.data?.result as Record<string, unknown> | null)?.traces != null;

  const handleSelectNotification = (actionKey: string) => {
    const config = NOTIFICATION_CONFIGS[actionKey];
    if (config) {
      setSelectedNotification(config);
      setParameterValue("");
      setConfirmDialogOpen(true);
    }
  };

  /** Execute a notification that uses org-level config (no parameter dialog). */
  const handleDirectNotification = (actionType: ActionType) => {
    executeAction.mutate({
      issueId,
      data: {
        action_type: actionType,
        action_category: "notification" as ActionCategory,
      },
    });
  };

  const handleConfirmExecute = () => {
    if (!selectedNotification || !parameterValue.trim()) return;

    executeAction.mutate({
      issueId,
      data: {
        action_type: selectedNotification.type,
        action_category: selectedNotification.category,
        parameters: {
          [selectedNotification.parameterKey]: parameterValue.trim(),
        },
      },
    });

    setConfirmDialogOpen(false);
    setSelectedNotification(null);
    setParameterValue("");
  };

  const handleCancelNotification = () => {
    setConfirmDialogOpen(false);
    setSelectedNotification(null);
    setParameterValue("");
  };

  /**
   * View cached investigation results without making an API call.
   */
  const handleViewCachedResult = useCallback(
    (actionKey: string) => {
      if (actionKey === "generate_incident_report" && latestReport.data?.result) {
        const result = latestReport.data.result as { report?: IncidentReport };
        setReportData(result.report ?? null);
        setReportLoading(false);
        setReportPanelOpen(true);
      } else if (actionKey === "search_similar_prompts" && latestSearch.data?.result) {
        const result = latestSearch.data.result as {
          traces?: SimilarTrace[];
          total_found?: number;
        };
        setSimilarTraces(result.traces ?? []);
        setSimilarTracesTotal(result.total_found ?? 0);
        setSimilarTracesDialogOpen(true);
      }
    },
    [latestReport.data, latestSearch.data]
  );

  /**
   * Execute an investigation action (optionally with force=true to bypass cache).
   */
  const handleExecuteInvestigation = useCallback(
    (actionKey: string, force = false) => {
      const config = INVESTIGATION_CONFIGS[actionKey];
      if (!config) return;

      // Open the panel/dialog immediately with loading state for reports
      if (actionKey === "generate_incident_report") {
        setReportLoading(true);
        setReportData(null);
        setReportPanelOpen(true);
      }

      executeAction.mutate(
        {
          issueId,
          data: {
            action_type: config.type,
            action_category: config.category,
          },
          force,
        },
        {
          onSettled: (data, error) => {
            if (actionKey === "generate_incident_report") {
              setReportLoading(false);
            }

            if (error || !data || data.status !== "COMPLETED" || !data.result) {
              if (actionKey === "generate_incident_report" && !error) {
                // Keep the panel open with no data to show the error state
              }
              return;
            }

            if (actionKey === "search_similar_prompts") {
              const result = data.result as {
                traces?: SimilarTrace[];
                total_found?: number;
              };
              setSimilarTraces(result.traces ?? []);
              setSimilarTracesTotal(result.total_found ?? 0);
              setSimilarTracesDialogOpen(true);
            } else if (actionKey === "generate_incident_report") {
              const result = data.result as { report?: IncidentReport };
              setReportData(result.report ?? null);
            }
          },
        }
      );
    },
    [issueId, executeAction]
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Play className="h-4 w-4" />
            Run an Action
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {/* Notifications */}
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2">
              <Bell className="h-3.5 w-3.5" />
              Notifications
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => handleDirectNotification("notify_slack")}
              disabled={!slackConfigured || executeAction.isPending}
            >
              <Bell className="h-4 w-4 mr-2" />
              {slackConfigured ? "Send Slack Alert" : "Send Slack Alert (Not Configured)"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleDirectNotification("notify_teams")}
              disabled={!teamsConfigured || executeAction.isPending}
            >
              <Users className="h-4 w-4 mr-2" />
              {teamsConfigured ? "Send Teams Alert" : "Send Teams Alert (Not Configured)"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSelectNotification("notify_email")}>
              <Mail className="h-4 w-4 mr-2" />
              Send Email Alert
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Investigation */}
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2">
              <Search className="h-3.5 w-3.5" />
              Investigation
            </DropdownMenuLabel>

            {/* Similar Prompts */}
            <DropdownMenuItem
              onClick={() =>
                hasSearchCache
                  ? handleViewCachedResult("search_similar_prompts")
                  : handleExecuteInvestigation("search_similar_prompts")
              }
              disabled={!hasSearchCache && executeAction.isPending}
            >
              {hasSearchCache ? (
                <Eye className="h-4 w-4 mr-2" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              {hasSearchCache
                ? "View Similar Prompts"
                : executeAction.isPending
                  ? "Searching..."
                  : "Search Similar Prompts"}
            </DropdownMenuItem>

            {/* Incident Report */}
            <DropdownMenuItem
              onClick={() =>
                hasReportCache
                  ? handleViewCachedResult("generate_incident_report")
                  : handleExecuteInvestigation("generate_incident_report")
              }
              disabled={!hasReportCache && executeAction.isPending}
            >
              {hasReportCache ? (
                <Download className="h-4 w-4 mr-2" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              {hasReportCache
                ? "Export Incident Report"
                : executeAction.isPending
                  ? "Generating..."
                  : "Generate Incident Report"}
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Containment */}
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" />
              Containment
            </DropdownMenuLabel>
            <DropdownMenuItem disabled>
              <Lock className="h-4 w-4 mr-2" />
              <span className="text-muted-foreground">Block Prompt Pattern (Coming Soon)</span>
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <AlertTriangle className="h-4 w-4 mr-2" />
              <span className="text-muted-foreground">Quarantine Session (Coming Soon)</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Remediation */}
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2">
              <Wrench className="h-3.5 w-3.5" />
              Remediation
            </DropdownMenuLabel>
            <DropdownMenuItem disabled>
              <span className="text-muted-foreground">Coming soon</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Notification Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{selectedNotification?.label}</DialogTitle>
            <DialogDescription>{selectedNotification?.description}</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 p-1 space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="action-parameter">{selectedNotification?.parameterLabel}</Label>
              <Input
                id="action-parameter"
                value={parameterValue}
                onChange={(e) => setParameterValue(e.target.value)}
                placeholder={selectedNotification?.parameterPlaceholder}
              />
            </div>
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={handleCancelNotification}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmExecute}
              disabled={!parameterValue.trim() || executeAction.isPending}
            >
              {executeAction.isPending ? "Executing..." : "Execute"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Similar Traces Dialog */}
      <SimilarTracesDialog
        open={similarTracesDialogOpen}
        onOpenChange={setSimilarTracesDialogOpen}
        issueId={issueId}
        traces={similarTraces}
        totalFound={similarTracesTotal}
        onRegenerate={() => handleExecuteInvestigation("search_similar_prompts", true)}
        isRegenerating={executeAction.isPending}
      />

      {/* Incident Report Panel (Sheet) */}
      <IncidentReportPanel
        open={reportPanelOpen}
        onOpenChange={setReportPanelOpen}
        issueId={issueId}
        report={reportData}
        isLoading={reportLoading}
        onRegenerate={() => handleExecuteInvestigation("generate_incident_report", true)}
        isRegenerating={executeAction.isPending}
      />
    </>
  );
}
